import type { SupportedLanguage } from './index'

/**
 * Define localized routes here.
 * Each key maps to a route name, with translations for each language.
 *
 * Example:
 *   about: { it: 'chi-siamo', en: 'about-us' }
 *   → /it/chi-siamo, /en/about-us
 */
export const localizedRoutes = {
  home: {
    it: '',
    en: '',
    es: '',
    de: '',
    fr: '',
  },
  about: {
    it: 'chi-siamo',
    en: 'about',
    es: 'quienes-somos',
    de: 'ueber-uns',
    fr: 'a-propos',
  },
  contacts: {
    it: 'contatti',
    en: 'contacts',
    es: 'contacto',
    de: 'kontakt',
    fr: 'contact',
  },
  privacy: {
    it: 'privacy',
    en: 'privacy',
    es: 'privacidad',
    de: 'datenschutz',
    fr: 'confidentialite',
  },
  // Products
  product_firmo: {
    it: 'firmo-semplice',
    en: 'firmo-semplice',
    es: 'firmo-semplice',
    de: 'firmo-semplice',
    fr: 'firmo-semplice',
  },
  product_ai: {
    it: 'semplice-ai',
    en: 'semplice-ai',
    es: 'semplice-ai',
    de: 'semplice-ai',
    fr: 'semplice-ai',
  },
  product_pa: {
    it: 'semplice-pa',
    en: 'semplice-pa',
    es: 'semplice-pa',
    de: 'semplice-pa',
    fr: 'semplice-pa',
  },
  // Services
  service_dev: {
    it: 'sviluppo-software',
    en: 'software-development',
    es: 'desarrollo-software',
    de: 'softwareentwicklung',
    fr: 'developpement-logiciel',
  },
  service_integration: {
    it: 'system-integration',
    en: 'system-integration',
    es: 'integracion-sistemas',
    de: 'systemintegration',
    fr: 'integration-systeme',
  },
  service_transformation: {
    it: 'digital-transformation',
    en: 'digital-transformation',
    es: 'transformacion-digital',
    de: 'digitale-transformation',
    fr: 'transformation-digitale',
  },
  service_security: {
    it: 'cybersecurity',
    en: 'cybersecurity',
    es: 'ciberseguridad',
    de: 'cybersicherheit',
    fr: 'cybersecurite',
  },
  service_cloud: {
    it: 'cloud',
    en: 'cloud',
    es: 'cloud',
    de: 'cloud',
    fr: 'cloud',
  },
  service_consulting: {
    it: 'consulenza',
    en: 'consulting',
    es: 'consultoria',
    de: 'beratung',
    fr: 'conseil',
  },
  // Case studies
  realizzazioni: {
    it: 'realizzazioni',
    en: 'case-studies',
    es: 'proyectos',
    de: 'projekte',
    fr: 'realisations',
  },
  // FAQ
  faq: {
    it: 'faq',
    en: 'faq',
    es: 'faq',
    de: 'faq',
    fr: 'faq',
  },
} as const

export type RouteKey = keyof typeof localizedRoutes

/**
 * Get the localized path for a route
 */
export function getLocalizedPath(
  routeKey: RouteKey,
  lang: SupportedLanguage
): string {
  const path = localizedRoutes[routeKey][lang]
  return path ? `/${lang}/${path}` : `/${lang}`
}

/**
 * Find route key from a path segment
 */
export function findRouteKeyFromPath(
  pathSegment: string,
  lang: SupportedLanguage
): RouteKey | null {
  for (const [key, routes] of Object.entries(localizedRoutes)) {
    if (routes[lang] === pathSegment) {
      return key as RouteKey
    }
  }
  return null
}
